/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-google-charts';
import { colors } from '../../utils/colorsGrafica';
import { fechaHora } from '../../utils/parsers/date';

const options = {
  height: 600,
  width: 800,
  sankey: {
    node: {
      colors,
      label: {
        fontName: 'Arial',
        fontSize: 12,
        color: '#333'
      }
    },
    link: {
      colorMode: 'target',
      colors
    }
  },
  tooltip: {
    isHtml: true,
    textStyle: {
      fontSize: 12
    }
  }
};

const Sankey = ({ data, title, isNewFormat = false, loading = false, error = null }) => {
  const [dataset, setDataset] = useState();
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    let parsedData = [];
    let meta = null;

    // Si es el nuevo formato de datos de la API
    if (isNewFormat && data) {
      if (data.chartData) {
        parsedData = data.chartData;
        meta = data.metadata;
      } else if (data.nodes && data.links) {
        // Transformar datos de nodes/links a formato Google Charts
        parsedData = [['From', 'To', 'Weight']];
        
        // Crear mapa de nodos
        const nodeMap = {};
        data.nodes.forEach(node => {
          nodeMap[node.id] = node;
        });

        // Transformar links
        data.links.forEach(link => {
          const sourceNode = nodeMap[link.source];
          const targetNode = nodeMap[link.target];
          
          if (sourceNode && targetNode) {
            const sourceName = `${sourceNode.name} (${sourceNode.value}${sourceNode.unidades ? ' ' + sourceNode.unidades : ''})`;
            const targetName = `${targetNode.name} (${targetNode.value}${targetNode.unidades ? ' ' + targetNode.unidades : ''})`;
            
            parsedData.push([sourceName, targetName, link.value]);
          }
        });

        meta = data.metadata;
      }
    } 
    // Formato legacy existente
    else if (data && data.nivel) {
      if (data.nivel === 'proyecto') {
        const proyectoElemento = data.graficaSankey.elementos.map(
          (elemento) => [`${data.graficaSankey.nombre} (${data.graficaSankey.totalProyecto})`,
            `E. ${elemento.nombre} (${elemento.totalElemento})`, elemento.totalElemento]
        );
        const elementoTarjeta = data.graficaSankey.elementos.map(
          (elemento) => elemento.tarjetas.map(
            (tarjeta) => [`E. ${elemento.nombre} (${elemento.totalElemento})`,
              `T. ${tarjeta.nombre} (${tarjeta.totalTarjeta})`, tarjeta.totalTarjeta]
          )
        );
        const tarjetaSensores = data.graficaSankey.elementos.map(
          (elemento) => elemento.tarjetas.map(
            (tarjeta) => tarjeta.sensores.map(
              (sensor) => [`T. ${tarjeta.nombre} (${tarjeta.totalTarjeta})`,
                `${sensor.nombre} (${Number(sensor.data[0]?.total?.$numberDecimal)})`, Number(sensor.data[0]?.total?.$numberDecimal)]
            )
          )
        );
        parsedData = [['', '', 'Valor'], ...proyectoElemento, ...elementoTarjeta.flat(1), ...tarjetaSensores.flat(2)];
      }
      if (data.nivel === 'elemento') {
        const elementoTarjeta = data.graficaSankey.tarjetas.map(
          (tarjeta) => [`${data.graficaSankey.nombre} (${data.graficaSankey.totalElemento})`,
            `T. ${tarjeta.nombre} (${tarjeta.totalTarjeta})`, tarjeta.totalTarjeta]
        );

        const tarjetaSensores = data.graficaSankey.tarjetas.map(
          (tarjeta) => tarjeta.sensores.map(
            (sensor) => [`T. ${tarjeta.nombre} (${tarjeta.totalTarjeta})`,
              `${sensor.nombre} (${Number(sensor.data[0]?.total?.$numberDecimal)})`,
              Number(sensor.data[0]?.total?.$numberDecimal)]
          )
        );

        parsedData = [['', '', 'Valor'], elementoTarjeta.flat(), ...tarjetaSensores.flat(1)];
      }
      // Mantener código comentado para tarjeta si se necesita en el futuro
      // if (data.nivel === 'tarjeta') {
      //   const tarjetaSensores = data.graficaSankey.sensores.map(
      //     (sensor) => [data.graficaSankey.nombre, sensor.nombre, Number(sensor.data[0]?.total?.$numberDecimal)]
      //   );
      //   parsedData = [['', '', 'Valor'], ...tarjetaSensores];
      // }
    }

    setDataset(parsedData);
    setMetadata(meta);
  }, [data, isNewFormat]);

  if (loading) {
    return (
      <div className="card-grafica-container">
        <div className="card-grafica-container__header">
          <p className="card-grafica-container__title">{title}</p>
        </div>
        <div className="card-grafica-container__body-grafica-sankey">
          <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Cargando gráfica Sankey...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-grafica-container">
        <div className="card-grafica-container__header">
          <p className="card-grafica-container__title">{title}</p>
        </div>
        <div className="card-grafica-container__body-grafica-sankey">
          <div className="error-container" style={{ textAlign: 'center', padding: '2rem', color: '#e74c3c' }}>
            <p>Error al cargar la gráfica: {error.message || 'Error desconocido'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-grafica-container">
      <div className="card-grafica-container__header">
        <p className="card-grafica-container__title">{title}</p>
      </div>
      
      {/* Mostrar información de fechas si está disponible (formato legacy) */}
      {data?.fechaInicio && data?.fechaFin && (
        <div>
          <p className="text-reporte-2">
            {`(${fechaHora(data.fechaInicio)} - ${fechaHora(data.fechaFin)})`}
          </p>
        </div>
      )}

      {/* Mostrar metadatos si están disponibles (nuevo formato) */}
      {metadata && (
        <div className="sankey-metadata" style={{ padding: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
          {metadata.fechaConsulta && (
            <p>Fecha de consulta: {fechaHora(metadata.fechaConsulta)}</p>
          )}
          {metadata.totalRegistros && (
            <p>Registros consultados: {metadata.totalRegistros}</p>
          )}
          {metadata.unidadesPrincipales && metadata.unidadesPrincipales.length > 0 && (
            <p>Unidades: {metadata.unidadesPrincipales.join(', ')}</p>
          )}
          {metadata.sensoresCount && (
            <p>Total de sensores: {metadata.sensoresCount}</p>
          )}
        </div>
      )}

      <div className="card-grafica-container__body-grafica-sankey">
        {dataset?.length > 1 ? (
          <Chart
            chartType="Sankey"
            data={dataset}
            options={options}
          />
        ) : (
          <p className="text-nothing">
            Los sensores de la gráfica
            {' '}
            {title}
            {' '}
            no contienen datos
          </p>
        )}
      </div>
    </div>
  );
};

Sankey.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({
      // Formato legacy
      nivel: PropTypes.string,
      graficaSankey: PropTypes.shape(),
      fechaInicio: PropTypes.string,
      fechaFin: PropTypes.string
    }),
    PropTypes.shape({
      // Nuevo formato
      nodes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        level: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        originalId: PropTypes.string.isRequired,
        unidades: PropTypes.string,
        tipo: PropTypes.string,
        metadata: PropTypes.shape()
      })),
      links: PropTypes.arrayOf(PropTypes.shape({
        source: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        unidades: PropTypes.string.isRequired
      })),
      metadata: PropTypes.shape({
        totalRegistros: PropTypes.number,
        fechaConsulta: PropTypes.string,
        unidadesPrincipales: PropTypes.arrayOf(PropTypes.string),
        sensoresCount: PropTypes.number
      }),
      chartData: PropTypes.array
    })
  ]),
  title: PropTypes.string.isRequired,
  isNewFormat: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.object
};

export default Sankey;